import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientMessage, ServerMessage } from "@shared/protocol";
import { useWebSocket } from "./hooks/useWebSocket";
import { useAppStore } from "./store/appStore";
import { supabase } from "./lib/supabase";
import { Sidebar } from "./components/Sidebar";
import { TopBar } from "./components/TopBar";
import { StatusBar } from "./components/StatusBar";
import { PanelGrid } from "./components/PanelGrid";
import { WorkspaceModal } from "./components/WorkspaceModal";
import { TeamDrawer } from "./components/TeamPanel";
import { Login } from "./components/Login";

export function App() {
  const [token, setToken] = useState<string | null>(null);
  const [bootChecked, setBootChecked] = useState(false);

  // Restore existing Supabase session on boot.
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setToken(data.session?.access_token ?? null);
      setBootChecked(true);
    });
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      // Only react to events that actually represent a meaningful auth state
      // change. TOKEN_REFRESHED with a fresh session is a NO-OP for the
      // login UI; we still update `token` so the WS picks up the new bearer.
      console.debug("[auth] supabase event:", event, "hasSession=", !!session);
      if (event === "SIGNED_OUT") {
        setToken(null);
        return;
      }
      if (session?.access_token) {
        setToken(session.access_token);
      }
      // For any other event (e.g. transient null session during a token
      // refresh in flight), keep the existing token instead of dropping it.
    });
    return () => data.subscription.unsubscribe();
  }, []);

  if (!bootChecked) return null;
  if (!token) return <Login onSignIn={setToken} />;

  return <Workspace token={token} />;
}

function Workspace({ token }: { token: string }) {
  const {
    me, density, sidebarVariant, layout, wsModalOpen, teammateDrawer,
    setMe, setTeam, setWorkspaces, hydrateSessions, pushCommit, patchPanel,
  } = useAppStore();

  // Registry of agentId → writer fn (set by each TerminalPanel).
  const writers = useRef(new Map<string, (data: string) => void>());
  const registerWriter = useCallback((agentId: string, write: (data: string) => void) => {
    writers.current.set(agentId, write);
    return () => { writers.current.delete(agentId); };
  }, []);

  // sendRef must exist before handle uses it. The actual send fn is assigned
  // after useWebSocket below; handle calls go through .current so we always
  // see the freshest send.
  const sendRef = useRef<((msg: ClientMessage) => boolean) | null>(null);

  const handle = useCallback((msg: ServerMessage) => {
    switch (msg.type) {
      case "auth:ok":
        setMe(msg.payload.user);
        sendRef.current?.({ type: "workspace:list" });
        break;
      case "auth:error":
        // The server rejected our token. Most likely cause is true expiry;
        // try to refresh ONCE before giving up — Supabase will mint a new
        // access_token from the refresh_token and our state machine picks
        // it up via onAuthStateChange (TOKEN_REFRESHED).
        console.warn("[auth]", msg.payload.reason, "— attempting refresh");
        supabase.auth.refreshSession().then(({ data, error }) => {
          if (error || !data.session) {
            console.error("[auth] refresh failed, signing out locally", error);
            supabase.auth.signOut({ scope: "local" });
          }
          // On success, onAuthStateChange already updated `token`.
        });
        break;
      case "team:status":
        setTeam(msg.payload.users);
        break;
      case "workspace:list":
        setWorkspaces(msg.payload.workspaces);
        break;
      case "agent:list":
      case "team:sessions":
        // Both message kinds feed the same panels store; ownerSupabaseId on
        // each session lets the UI distinguish own vs. teammate.
        hydrateSessions(msg.payload.sessions);
        break;
      case "agent:output":
        writers.current.get(msg.payload.agentId)?.(msg.payload.data);
        break;
      case "agent:status":
        patchPanel(msg.payload.agentId, { status: msg.payload.status });
        if (msg.payload.recap) patchPanel(msg.payload.agentId, { recap: msg.payload.recap });
        break;
      case "git:commit":
        pushCommit(msg.payload);
        break;
      case "git:status:result":
        // patch the workspace branch info when we get a status result
        useAppStore.setState((s) => ({
          workspaces: s.workspaces.map((w) =>
            w.path === msg.payload.workspacePath ? { ...w, branch: msg.payload.branch } : w
          ),
        }));
        break;
      case "error":
        console.warn("[server]", msg.payload.reason);
        break;
    }
  }, [setMe, setTeam, setWorkspaces, hydrateSessions, pushCommit, patchPanel]);

  const { send, status } = useWebSocket({ token, onMessage: handle });
  sendRef.current = send;

  // Heartbeat to keep WS alive through reverse proxies.
  useEffect(() => {
    const id = setInterval(() => send({ type: "ping" } as ClientMessage), 30_000);
    return () => clearInterval(id);
  }, [send]);

  const onSpawnAgent = useCallback(() => spawnNewAgent(send), [send]);

  // Global keybindings.
  // We use ⌘⇧N (Cmd+Shift+N) — bare ⌘N can't be prevented in Chrome and would
  // open a new browser window.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "n") {
        e.preventDefault();
        onSpawnAgent();
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        // future: open command palette
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSpawnAgent]);

  return (
    <div className={`app ${sidebarVariant === "team" ? "team-focus" : ""} ${density === "kompakt" ? "density-compact" : ""}`}
         data-layout={layout}>
      <Sidebar onSpawnAgent={onSpawnAgent} />
      <TopBar />
      <PanelGrid send={send} registerWriter={registerWriter} />
      <StatusBar />

      {wsModalOpen && <WorkspaceModal send={send} />}
      {teammateDrawer && <TeamDrawer send={send} />}

      {status !== "open" && (
        <div style={{
          position: "fixed", bottom: 40, right: 16,
          padding: "0.4rem 0.7rem", fontSize: 11,
          background: "var(--color-bg-elev2)",
          border: "1px solid var(--color-line-strong)",
          borderRadius: "var(--radius-pill)",
          color: "var(--color-fg-muted)",
          fontFamily: "var(--font-mono)",
        }}>
          {status === "connecting" ? "verbinde…" : "getrennt — reconnect…"}
        </div>
      )}
      {!me && (
        <div style={{
          position: "fixed", bottom: 40, left: "calc(var(--sidebar-w) + 16px)",
          padding: "0.4rem 0.7rem", fontSize: 11, pointerEvents: "none",
          background: "var(--color-bg-elev2)", border: "1px solid var(--color-line-strong)",
          borderRadius: "var(--radius-pill)", color: "var(--color-fg-muted)",
          fontFamily: "var(--font-mono)",
        }}>auth…</div>
      )}
    </div>
  );
}

function spawnNewAgent(send: (m: ClientMessage) => boolean) {
  const s = useAppStore.getState();
  const ws = s.workspaces.find((w) => w.id === s.activeWorkspace) ?? s.workspaces[0];
  if (!ws) return;
  const agentId = `a_${Math.random().toString(36).slice(2, 8)}`;
  const task = window.prompt("Aufgabe für die neue Session:", "Neue Session") || "Neue Session";
  useAppStore.getState().upsertPanel({
    agentId,
    task,
    workspacePath: ws.path,
    workspaceName: ws.name,
    status: "thinking",
    startedAt: Date.now(),
    active: true,
    ownerSupabaseId: useAppStore.getState().me?.supabaseId,
  });
  useAppStore.getState().setActivePanel(agentId);
  send({ type: "agent:spawn", payload: { agentId, workspacePath: ws.path, task } });
}
